import React, { Component } from 'react';
import { toast } from 'react-toastify';

import * as api from '../api';
import { ESCALATE_REASON_LABELS, APPS, DEFAULT_APP, PROMPT_IDS } from '../constants';

const SAMPLE_QUESTIONS = [
    'Nạp iCoin bằng thẻ cào thì làm thế nào?',
    'Làm sao để thu âm đơn ca?',
    'Tôi muốn hoàn tiền',
    'Thời tiết Hà Nội hôm nay thế nào?',
];

/**
 * Nội dung hướng dẫn chatbot (giọng điệu, luật) và ô chạy thử.
 *
 * Chạy thử gọi đúng đường sinh câu trả lời thật nhưng không gửi tin cho ai, nên dùng để duyệt nội
 * dung trước khi bật chatbot.
 */
export default class PromptTab extends Component {
    constructor(props) {
        super(props);
        this.state = {
            promptId: PROMPT_IDS[0].id,
            content: '',
            loading: true,
            saving: false,
            previewMessage: '',
            previewLanguage: DEFAULT_APP,
            previewResult: null,
            previewRunning: false,
        };
    }

    componentDidMount() {
        this.loadPrompt(this.state.promptId);
    }

    loadPrompt = (promptId) => {
        this.setState({ promptId, loading: true, content: '' });
        api.getPrompt(promptId)
            .then((prompt) => this.setState({ content: (prompt && prompt.content) || '', loading: false }))
            .catch(() => {
                // Chưa cấu hình thì backend trả lỗi; coi như nội dung rỗng để nhân viên soạn mới.
                this.setState({ content: '', loading: false });
            });
    };

    handleSave = () => {
        const { promptId, content } = this.state;
        if (!content.trim()) {
            toast.warning('Nội dung đang trống.');
            return;
        }

        this.setState({ saving: true });
        api.updatePrompt(promptId, content)
            .then(() => {
                this.setState({ saving: false });
                toast.success('Đã lưu nội dung.');
            })
            .catch(() => {
                this.setState({ saving: false });
                toast.error('Không lưu được nội dung.');
            });
    };

    handlePreview = (message) => {
        const question = (message !== undefined ? message : this.state.previewMessage) || '';
        if (!question.trim()) {
            toast.warning('Nhập một câu hỏi để thử.');
            return;
        }

        this.setState({ previewMessage: question, previewRunning: true, previewResult: null });
        api.preview(question, this.state.previewLanguage)
            .then((result) => this.setState({ previewResult: result, previewRunning: false }))
            .catch((error) => {
                this.setState({ previewRunning: false });
                toast.error(error.message || 'Chạy thử thất bại.');
            });
    };

    renderPreviewResult() {
        const { previewResult } = this.state;
        if (!previewResult) return null;

        const needAgent = previewResult.needAgent === true;

        if (needAgent) {
            return (
                <div className="cs-result cs-result--agent">
                    <div className="cs-result__head" style={{ color: 'var(--cs-danger)' }}>
                        <i className="fa-solid fa-user-tie" />
                        Chuyển nhân viên
                    </div>
                    <div className="cs-result__body">
                        {ESCALATE_REASON_LABELS[previewResult.reason] || previewResult.reason || '—'}
                    </div>
                    <div className="cs-result__meta">
                        Người dùng sẽ nhận câu chốt lịch sự, và chỉ nhận một lần trong mỗi đêm.
                    </div>
                </div>
            );
        }

        const faqIds = previewResult.faqIds || [];

        return (
            <div className="cs-result cs-result--ok">
                <div className="cs-result__head" style={{ color: 'var(--cs-success)' }}>
                    <i className="fa-solid fa-robot" />
                    Chatbot tự trả lời
                </div>
                <div className="cs-result__body">{previewResult.reply}</div>
                <div className="cs-result__meta">
                    Độ chắc chắn: <b>{previewResult.confidence ?? '—'}</b> · Dựa trên FAQ:{' '}
                    {faqIds.length > 0
                        ? faqIds.map((id) => <span key={id} className="cs-code mr-1">{id}</span>)
                        : <span style={{ color: 'var(--cs-danger)', fontWeight: 600 }}>không có</span>}
                </div>
            </div>
        );
    }

    render() {
        const {
            promptId, content, loading, saving,
            previewMessage, previewLanguage, previewRunning,
        } = this.state;

        return (
            <div className="cs-split">
                <div className="cs-card" style={{ marginBottom: 0 }}>
                    <div className="cs-card__head">
                        <div>
                            <h2><i className="fa-solid fa-file-lines mr-2" />Nội dung hướng dẫn chatbot</h2>
                            <div className="cs-card__desc">
                                Giọng điệu, luật và khuôn kết quả. Đây là thứ quyết định chatbot cư xử thế nào.
                            </div>
                        </div>
                    </div>

                    <div className="cs-card__body">
                        <div className="cs-field">
                            <label>Chọn nội dung</label>
                            <select
                                className="form-control"
                                value={promptId}
                                onChange={(e) => this.loadPrompt(e.target.value)}
                            >
                                {PROMPT_IDS.map((item) => (
                                    <option key={item.id} value={item.id}>{item.label}</option>
                                ))}
                            </select>
                        </div>

                        {loading ? (
                            <div className="cs-loading">Đang tải…</div>
                        ) : (
                            <div className="cs-field" style={{ marginBottom: 0 }}>
                                <textarea
                                    className="form-control"
                                    rows="22"
                                    style={{
                                        fontFamily: 'SFMono-Regular, Menlo, Consolas, monospace',
                                        fontSize: '13px',
                                    }}
                                    value={content}
                                    onChange={(e) => this.setState({ content: e.target.value })}
                                />
                                <small className="cs-hint">
                                    Biến thay được: <span className="cs-code">{'{{faq}}'}</span>{' '}
                                    <span className="cs-code">{'{{userName}}'}</span>{' '}
                                    <span className="cs-code">{'{{currentTime}}'}</span>{' '}
                                    <span className="cs-code">{'{{appName}}'}</span>{' '}
                                    <span className="cs-code">{'{{userLanguage}}'}</span>{' '}
                                    <span className="cs-code">{'{{workingHours}}'}</span>
                                    <br />
                                    Đặt phần cố định (giọng điệu, luật, <span className="cs-code">{'{{faq}}'}</span>)
                                    lên <b>trước</b> và <span className="cs-code">{'{{userName}}'}</span>,{' '}
                                    <span className="cs-code">{'{{currentTime}}'}</span> xuống <b>cuối</b> —
                                    phần đầu giống nhau giữa các lượt thì mô hình tái dùng được, giảm chi phí
                                    đáng kể.
                                    <br />
                                    Biến viết trong nội dung FAQ <b>không</b> được thay, chỉ có tác dụng ở đây.
                                </small>
                            </div>
                        )}
                    </div>

                    <div className="cs-card__foot">
                        <button
                            type="button"
                            className="btn btn-primary mr-2"
                            onClick={this.handleSave}
                            disabled={saving || loading}
                        >
                            <i className="fa-solid fa-floppy-disk mr-1" />
                            {saving ? 'Đang lưu…' : 'Lưu nội dung'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={() => this.loadPrompt(promptId)}
                        >
                            Tải lại
                        </button>
                    </div>
                </div>

                <div className="cs-card" style={{ marginBottom: 0 }}>
                    <div className="cs-card__head">
                        <div>
                            <h2><i className="fa-solid fa-flask mr-2" />Chạy thử</h2>
                            <div className="cs-card__desc">
                                Gọi đúng đường sinh câu trả lời thật nhưng <b>không gửi tin cho ai</b>. Dùng
                                để duyệt nội dung trước khi bật chatbot.
                            </div>
                        </div>
                    </div>

                    <div className="cs-card__body">
                        <div className="cs-field">
                            <label>Ứng dụng</label>
                            <select
                                className="form-control"
                                value={previewLanguage}
                                onChange={(e) => this.setState({ previewLanguage: e.target.value })}
                            >
                                {APPS.map((app) => (
                                    <option key={app.value} value={app.value}>{app.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="cs-field">
                            <label>Câu hỏi của người dùng</label>
                            <textarea
                                className="form-control"
                                rows="3"
                                placeholder="nạp icoin bằng momo sao vậy"
                                value={previewMessage}
                                onChange={(e) => this.setState({ previewMessage: e.target.value })}
                            />
                            <small className="cs-hint">Hoặc bấm một câu mẫu bên dưới:</small>
                            <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {SAMPLE_QUESTIONS.map((question) => (
                                    <button
                                        type="button"
                                        key={question}
                                        className="btn btn-sm btn-outline-secondary"
                                        disabled={previewRunning}
                                        onClick={() => this.handlePreview(question)}
                                    >
                                        {question}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => this.handlePreview()}
                            disabled={previewRunning}
                        >
                            <i className="fa-solid fa-play mr-1" />
                            {previewRunning ? 'Đang chạy…' : 'Chạy thử'}
                        </button>

                        {this.renderPreviewResult()}
                    </div>
                </div>
            </div>
        );
    }
}
